import { useEffect, useState } from "react";
import axios from "axios";
import { TicketPercent, Plus, Trash2, CheckCircle2 } from "lucide-react";
import { API_BASE } from "../../lib/site";

const inputCls =
    "w-full rounded-xl border-2 border-[#1A231E]/12 bg-white px-4 py-2.5 text-sm font-semibold text-[#1A231E] focus:outline-none focus:border-[#F57A3A] transition-colors";

export default function AdminCupons() {
    const [cupons, setCupons] = useState([]);
    const [msg, setMsg] = useState(null);
    const [form, setForm] = useState({ codigo: "", descricao: "", desconto_pct: 10, validade: "" });

    const load = () =>
        axios.get(`${API_BASE}/cupons/todos`, { withCredentials: true }).then(({ data }) => setCupons(data)).catch(() => {});

    useEffect(() => {
        load();
    }, []);

    const create = async (e) => {
        e.preventDefault();
        setMsg(null);
        try {
            await axios.post(
                `${API_BASE}/cupons`,
                { ...form, desconto_pct: parseFloat(form.desconto_pct) || 0, validade: form.validade || null },
                { withCredentials: true }
            );
            setForm({ codigo: "", descricao: "", desconto_pct: 10, validade: "" });
            setMsg({ ok: true, text: "Cupom criado e disponível no app!" });
            load();
        } catch (err) {
            setMsg({ ok: false, text: err.response?.data?.detail || "Erro ao criar cupom" });
        }
    };

    const toggle = async (c) => {
        await axios.put(`${API_BASE}/cupons/${c.id}`, { ...c, ativo: !c.ativo }, { withCredentials: true });
        load();
    };

    const excluir = async (c) => {
        await axios.delete(`${API_BASE}/cupons/${c.id}`, { withCredentials: true });
        load();
    };

    return (
        <section id="admin-cupons" data-testid="admin-cupons-section" className="scroll-mt-28">
            <div className="flex items-center gap-3">
                <TicketPercent className="w-6 h-6 text-[#0E6B3F]" />
                <h2 className="text-2xl font-extrabold">Cupons e promoções</h2>
            </div>

            <form onSubmit={create} data-testid="admin-cupom-form" className="mt-5 rounded-[1.75rem] bg-white border-2 border-[#1A231E]/12 sticker-soft p-6 grid sm:grid-cols-2 lg:grid-cols-5 gap-3.5">
                <input data-testid="admin-cupom-codigo" required value={form.codigo} onChange={(e) => setForm({ ...form, codigo: e.target.value.toUpperCase() })} placeholder="CÓDIGO" className={`${inputCls} uppercase`} />
                <input data-testid="admin-cupom-descricao" required value={form.descricao} onChange={(e) => setForm({ ...form, descricao: e.target.value })} placeholder="Descrição" className={`${inputCls} lg:col-span-2`} />
                <input data-testid="admin-cupom-desconto" required type="number" min="1" max="100" value={form.desconto_pct} onChange={(e) => setForm({ ...form, desconto_pct: e.target.value })} placeholder="% desc." className={inputCls} />
                <div className="flex gap-2.5">
                    <input data-testid="admin-cupom-validade" type="date" value={form.validade} onChange={(e) => setForm({ ...form, validade: e.target.value })} className={inputCls} />
                    <button type="submit" data-testid="admin-cupom-submit" aria-label="Criar cupom" className="w-11 h-11 rounded-xl grid place-items-center bg-[#F57A3A] hover:bg-[#E06324] text-[#F7F4EC] transition-colors shrink-0">
                        <Plus className="w-5 h-5" />
                    </button>
                </div>
            </form>

            {msg && (
                <p data-testid="admin-cupons-message" className={`mt-4 rounded-xl px-4 py-3 text-sm font-semibold border-2 ${msg.ok ? "bg-green-50 border-green-200 text-green-700" : "bg-red-50 border-red-200 text-red-600"}`}>
                    {msg.ok ? <CheckCircle2 className="w-4 h-4 inline mr-1.5 -mt-0.5" /> : null}
                    {msg.text}
                </p>
            )}

            <div className="mt-5 space-y-2.5" data-testid="admin-cupons-list">
                {cupons.map((c) => (
                    <div key={c.id} data-testid="admin-cupom-item" className="flex items-center gap-4 rounded-2xl bg-white border-2 border-[#1A231E]/10 px-4.5 px-4 py-3.5">
                        <span className="w-11 h-11 rounded-xl bg-[#F1C75B] grid place-items-center shrink-0 rotate-3">
                            <TicketPercent className="w-5 h-5 text-[#1A231E]" />
                        </span>
                        <div className="min-w-0 flex-1">
                            <p className="font-black text-[#F57A3A]">{c.codigo} <span className="text-[#0E6B3F]">-{c.desconto_pct}%</span></p>
                            <p className="text-sm font-semibold text-[#1A231E]/60 truncate">{c.descricao}</p>
                            {c.validade && <p className="text-xs font-bold text-[#1A231E]/40">Válido até {new Date(`${c.validade}T12:00:00`).toLocaleDateString("pt-BR")}</p>}
                        </div>
                        <button onClick={() => toggle(c)} data-testid={`cupom-toggle-${c.id}`} className={`text-[10px] font-extrabold uppercase tracking-wider px-2.5 py-1 rounded-full shrink-0 ${c.ativo ? "bg-[#0E6B3F]/10 text-[#0E6B3F]" : "bg-red-100 text-red-500"}`}>
                            {c.ativo ? "Ativo" : "Inativo"}
                        </button>
                        <button onClick={() => excluir(c)} data-testid={`cupom-delete-${c.id}`} aria-label={`Excluir ${c.codigo}`} className="w-9 h-9 rounded-lg grid place-items-center text-red-500 hover:bg-red-50 transition-colors shrink-0">
                            <Trash2 className="w-4 h-4" />
                        </button>
                    </div>
                ))}
                {cupons.length === 0 && (
                    <p className="rounded-2xl border-2 border-dashed border-[#1A231E]/15 px-6 py-8 text-center text-sm font-semibold text-[#1A231E]/50">Nenhum cupom criado.</p>
                )}
            </div>
        </section>
    );
}
